import React from 'react';
import { motion } from 'framer-motion';
import Seo from '../../components/Seo';
import { seo } from '../../seo/seoData';
import { useTranslation } from 'react-i18next';
import { FileSignature, ArrowLeft, Euro, Clock, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import LegalDraftBanner from '../../components/LegalDraftBanner';

// =============================================================================
// LEGAL DRAFT — REQUIRES AVOCAT REVIEW BEFORE ANY CHAUFFEUR ONBOARDING
// =============================================================================
// The Chauffeur Partnership Agreement is the highest URSSAF / requalification
// risk document on the platform. EVERY clause must be cross-checked with an
// avocat droit social before it is presented to a single chauffeur for
// signature, and ideally validated against the hybride model previously
// discussed (apporteur d'affaires B2C + sous-traitance B2B).
//
// Specific clauses requiring deep legal review:
//
//   - Article 3 — 10% commission terms: the rate itself is acceptable, but
//     the "Services included in the commission" list (Art 3.2) bundles
//     payment processing, marketing, customer support, etc. into a single
//     all-inclusive fee. Avocat must confirm this bundling does not create
//     an "imposed economic dependence" finding under Cassation criteria.
//   - Article 4 — Weekly payment schedule (every Monday for the prior
//     week's rides): looks like salary periodicity (paie hebdomadaire). For
//     a true commercial intermediation the chauffeur's invoice should drive
//     the payment, not a platform-imposed weekly cycle. Restate as
//     "Black Matte will reverse the chauffeur's earnings within X business
//     days of receiving the chauffeur's invoice for the period."
//   - Article 2 — "Liberté d'organisation" / "Travailler pour d'autres
//     plateformes simultanément": the absence of an exclusivity clause is
//     correct and important. The agreement must NEVER add any exclusivity,
//     non-compete, or right of first refusal clause; doing so would be
//     near-fatal for the auto-entrepreneur status.
//   - Article 7.3 — "mise en demeure restée sans effet pendant 15 jours"
//     before termination: the 15-day cure period is reasonable but the
//     "manquement aux obligations du règlement chauffeurs" trigger imports
//     all the disciplinary risk from the règlement document. Termination
//     should be tied to objective contractual breaches only (license expired,
//     insurance lapsed, fraud) — not to qualitative behaviour rules.
//   - Article 5 obligations: "Respecter le règlement chauffeurs" cross-
//     references the Code of Conduct with its level-1-to-4 sanction system.
//     Once that document is rewritten as a Charte, this reference must be
//     updated accordingly.
//   - Article 10 — "tribunaux de Nantes" jurisdiction clause: works for
//     B2B (independent contractor), but must be confirmed for the typical
//     auto-entrepreneur counterparty profile.
//   - Coordination with the hybride architecture: clarify which courses fall
//     under "apporteur d'affaires B2C" (commission on platform-billed rides)
//     versus "sous-traitance B2B" (subcontracting from Black Matte's
//     corporate contracts). The current text mixes both regimes.
//
// FR is authoritative; non-FR versions are courtesy translations marked via
// LegalDraftBanner.
// =============================================================================

const ChauffeurContractPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="chauffeur-contract-page">
          <Seo {...seo.chauffeurContract} />
      <LegalDraftBanner />
      <section className="py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Back link */}
            <Link to="/" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" />
              {t('legalPages.common.backToHome')}
            </Link>

            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center">
                <FileSignature className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
                  {t('legalPages.chauffeurContract.title')}
                </h1>
                <p className="text-neutral-500 text-sm mt-1">{t('legalPages.common.lastUpdated')}</p>
              </div>
            </div>

            {/* Key Points */}
            <div className="grid md:grid-cols-3 gap-4 mb-12">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
                <Euro className="w-8 h-8 text-green-400 mx-auto mb-3" />
                <h3 className="font-heading font-semibold text-white mb-1">{t('legalPages.chauffeurContract.keyPoints.commissionTitle')}</h3>
                <p className="text-2xl font-bold text-green-400">10%</p>
                <p className="text-neutral-500 text-xs mt-1">{t('legalPages.chauffeurContract.keyPoints.commissionDesc')}</p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
                <Clock className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                <h3 className="font-heading font-semibold text-white mb-1">{t('legalPages.chauffeurContract.keyPoints.paymentTitle')}</h3>
                <p className="text-2xl font-bold text-blue-400">{t('legalPages.chauffeurContract.keyPoints.paymentValue')}</p>
                <p className="text-neutral-500 text-xs mt-1">{t('legalPages.chauffeurContract.keyPoints.paymentDesc')}</p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
                <CheckCircle className="w-8 h-8 text-purple-400 mx-auto mb-3" />
                <h3 className="font-heading font-semibold text-white mb-1">{t('legalPages.chauffeurContract.keyPoints.statusTitle')}</h3>
                <p className="text-2xl font-bold text-purple-400">{t('legalPages.chauffeurContract.keyPoints.statusValue')}</p>
                <p className="text-neutral-500 text-xs mt-1">{t('legalPages.chauffeurContract.keyPoints.statusDesc')}</p>
              </div>
            </div>

            <div className="prose prose-invert max-w-none space-y-10">
              {/* Préambule */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.preambule.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.preambule.p1')}</p>
                <p className="text-neutral-400 leading-relaxed mt-4">{t('legalPages.chauffeurContract.preambule.p2')}</p>
              </section>

              {/* Article 1 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.art1.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.art1.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4, 5].map(i => <li key={i}>{t(`legalPages.chauffeurContract.art1.list${i}`)}</li>)}
                </ul>
              </section>

              {/* Article 2 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.art2.title')}</h2>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t('legalPages.chauffeurContract.art2.sub21Title')}</h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.art2.sub21Body')}</p>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t('legalPages.chauffeurContract.art2.sub22Title')}</h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.art2.sub22Intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4].map(i => <li key={i}>{t(`legalPages.chauffeurContract.art2.sub22List${i}`)}</li>)}
                </ul>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t('legalPages.chauffeurContract.art2.sub23Title')}</h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.art2.sub23Body')}</p>
              </section>

              {/* Article 3 - Commission */}
              <section className="bg-green-500/5 border border-green-500/20 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.art3.title')}</h2>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t('legalPages.chauffeurContract.art3.sub31Title')}</h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.art3.sub31Body')}</p>

                <div className="bg-black/30 rounded-xl p-4 mt-4">
                  <p className="text-neutral-400 text-sm">{t('legalPages.chauffeurContract.art3.sub31Example')}</p>
                </div>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t('legalPages.chauffeurContract.art3.sub32Title')}</h3>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4, 5, 6].map(i => <li key={i}>{t(`legalPages.chauffeurContract.art3.sub32List${i}`)}</li>)}
                </ul>
              </section>

              {/* Article 4 - Paiement */}
              <section className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.art4.title')}</h2>

                {[1, 2, 3].map(i => (
                  <React.Fragment key={i}>
                    <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t(`legalPages.chauffeurContract.art4.sub4${i}Title`)}</h3>
                    <p className="text-neutral-400 leading-relaxed">{t(`legalPages.chauffeurContract.art4.sub4${i}Body`)}</p>
                  </React.Fragment>
                ))}
              </section>

              {/* Article 5 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.art5.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.art5.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4, 5, 6].map(i => <li key={i}>{t(`legalPages.chauffeurContract.art5.list${i}`)}</li>)}
                </ul>
              </section>

              {/* Article 6 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.art6.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.art6.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4, 5].map(i => <li key={i}>{t(`legalPages.chauffeurContract.art6.list${i}`)}</li>)}
                </ul>
              </section>

              {/* Article 7 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.art7.title')}</h2>

                {[1, 2, 3, 4].map(i => (
                  <React.Fragment key={i}>
                    <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t(`legalPages.chauffeurContract.art7.sub7${i}Title`)}</h3>
                    <p className="text-neutral-400 leading-relaxed">{t(`legalPages.chauffeurContract.art7.sub7${i}Body`)}</p>
                  </React.Fragment>
                ))}
              </section>

              {/* Article 8 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.art8.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.art8.body')}</p>
              </section>

              {/* Article 9 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.art9.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.art9.body')}</p>
              </section>

              {/* Article 10 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.art10.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurContract.art10.p1')}</p>
                <p className="text-neutral-400 leading-relaxed mt-4">{t('legalPages.chauffeurContract.art10.p2')}</p>
              </section>

              {/* Contact */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurContract.contactPartners.title')}</h2>
                <div className="text-neutral-400 space-y-1">
                  <p>Email : chauffeurs@blackmatte.fr</p>
                  <p>Téléphone : 09 39 24 23 20</p>
                  <p>Nantes (44), France</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ChauffeurContractPage;
